import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type Ref, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { Bold, Heading, Italic, Link2, List, ListOrdered, Redo2, Undo2, type LucideIcon } from 'lucide-react'
import { usePopover } from '../../components/ui/usePopover'
import { normalizeLinkInput } from './emailContent'
// The app loads upright Inter only and sets font-synthesis: none, so without
// the real italic face <i>/<em> in the editor would render upright.
import '@fontsource-variable/inter/wght-italic.css'

/*
 * Opt the editor out of in-page writing helpers (Grammarly, LanguageTool,
 * Chrome writing suggestions) that float their own formatting/suggestion UI
 * over the text. The toolbar is the only formatting UI.
 */
const NO_WRITING_HELPERS = {
  'data-gramm': 'false',
  'data-gramm_editor': 'false',
  'data-enable-grammarly': 'false',
  'data-lt-active': 'false',
  writingsuggestions: 'false',
} as const

type Format = { bold: boolean; italic: boolean; heading: boolean; bullets: boolean; numbers: boolean; link: boolean }

const NO_FORMAT: Format = { bold: false, italic: false, heading: false, bullets: false, numbers: false, link: false }

/* execCommand is the browser's own editing engine: it keeps native undo/redo
   and selection behaviour. It is deprecated but still implemented everywhere,
   and every call is guarded so a missing command is a no-op, not a crash. */
function exec(command: string, value?: string): void {
  try {
    document.execCommand?.(command, false, value)
  } catch {
    /* unsupported command */
  }
}

function queryState(command: string): boolean {
  try {
    return document.queryCommandState?.(command) ?? false
  } catch {
    return false
  }
}

function queryBlock(): string {
  try {
    return (document.queryCommandValue?.('formatBlock') ?? '').toLowerCase().replace(/[<>]/g, '')
  } catch {
    return ''
  }
}

function isEmpty(editor: HTMLElement): boolean {
  return !editor.textContent?.trim() && !editor.querySelector('li, h1, h2, h3')
}

/**
 * Rich-text email body: a light toolbar over one editable surface. Links are
 * added from a small popover anchored to the Link button. The parent reads the
 * content with `serializeEmail(editorRef.current)`.
 */
export function EmailComposer({
  editorRef,
  labelledBy,
  placeholder,
  readOnly,
}: {
  editorRef: RefObject<HTMLDivElement | null>
  labelledBy: string
  placeholder: string
  readOnly: boolean
}) {
  const [format, setFormat] = useState<Format>(NO_FORMAT)
  const [empty, setEmpty] = useState(true)
  const linkPopover = usePopover<HTMLButtonElement>({ minWidth: 300 })
  const [editingLink, setEditingLink] = useState(false)
  const [linkValue, setLinkValue] = useState('')
  const [linkError, setLinkError] = useState('')
  const savedRange = useRef<Range | null>(null)
  const linkInputRef = useRef<HTMLInputElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const linkId = useId()

  const selectionInEditor = useCallback((): Range | null => {
    const selection = window.getSelection()
    const editor = editorRef.current
    if (!selection?.rangeCount || !editor) return null
    const range = selection.getRangeAt(0)
    return editor.contains(range.commonAncestorContainer) ? range : null
  }, [editorRef])

  const linkAtSelection = useCallback((): HTMLAnchorElement | null => {
    const range = selectionInEditor()
    const start = range?.startContainer
    const element = start instanceof Element ? start : start?.parentElement
    const link = element?.closest('a')
    return link && editorRef.current?.contains(link) ? link : null
  }, [editorRef, selectionInEditor])

  const refresh = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    setEmpty(isEmpty(editor))
    if (!selectionInEditor()) return
    const block = queryBlock()
    setFormat({
      bold: queryState('bold'),
      italic: queryState('italic'),
      heading: block === 'h1',
      bullets: queryState('insertUnorderedList'),
      numbers: queryState('insertOrderedList'),
      link: Boolean(linkAtSelection()),
    })
  }, [editorRef, linkAtSelection, selectionInEditor])

  useEffect(() => {
    document.addEventListener('selectionchange', refresh)
    return () => document.removeEventListener('selectionchange', refresh)
  }, [refresh])

  useEffect(() => {
    if (linkPopover.isOpen) linkInputRef.current?.focus()
  }, [linkPopover.isOpen])

  function restoreSelection() {
    const editor = editorRef.current
    if (!editor) return
    editor.focus()
    if (savedRange.current) {
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(savedRange.current)
    }
  }

  function run(command: string, value?: string) {
    if (readOnly) return
    editorRef.current?.focus()
    // Enter makes <p> paragraphs rather than <div>s.
    exec('defaultParagraphSeparator', 'p')
    exec(command, value)
    refresh()
  }

  function openLink() {
    if (readOnly) return
    savedRange.current = selectionInEditor()?.cloneRange() ?? null
    const existing = linkAtSelection()
    setEditingLink(Boolean(existing))
    setLinkValue(existing?.getAttribute('href')?.replace(/^mailto:/, '') ?? '')
    setLinkError('')
    linkPopover.open()
  }

  // Focus goes back to the text, where the user was writing.
  function closeLink(restore = true) {
    linkPopover.close(false)
    setLinkError('')
    if (restore) restoreSelection()
  }

  function applyLink() {
    const href = normalizeLinkInput(linkValue)
    if (!href) {
      setLinkError('Enter a web address or an email address.')
      return
    }
    restoreSelection()
    const existing = linkAtSelection()
    const range = selectionInEditor()
    if (existing && range?.collapsed) {
      // Editing a link with the caret inside it: select the whole link first.
      const whole = document.createRange()
      whole.selectNodeContents(existing)
      window.getSelection()?.removeAllRanges()
      window.getSelection()?.addRange(whole)
    }
    if (selectionInEditor()?.collapsed) {
      // Nothing selected: insert the address itself as the link text.
      const text = document.createTextNode(href.replace(/^mailto:/, ''))
      const anchor = document.createElement('a')
      anchor.href = href
      anchor.append(text)
      exec('insertHTML', anchor.outerHTML)
    } else {
      exec('createLink', href)
    }
    closeLink(false)
    refresh()
  }

  function removeLink() {
    restoreSelection()
    const existing = linkAtSelection()
    if (existing) {
      const whole = document.createRange()
      whole.selectNodeContents(existing)
      window.getSelection()?.removeAllRanges()
      window.getSelection()?.addRange(whole)
    }
    exec('unlink')
    closeLink(false)
    refresh()
  }

  // Arrow keys move between toolbar buttons; only one is in the Tab order.
  function onToolbarKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const buttons = [...(toolbarRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])]
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement)
    if (at < 0) return
    const next =
      event.key === 'ArrowRight' ? (at + 1) % buttons.length
      : event.key === 'ArrowLeft' ? (at - 1 + buttons.length) % buttons.length
      : event.key === 'Home' ? 0
      : event.key === 'End' ? buttons.length - 1
      : -1
    if (next < 0) return
    event.preventDefault()
    buttons.forEach((button, index) => (button.tabIndex = index === next ? 0 : -1))
    buttons[next].focus()
  }

  function onEditorKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey) return
    const key = event.key.toLowerCase()
    // Handled here, not left to the browser, so the toolbar state updates at once.
    if (key === 'b' || key === 'i') {
      event.preventDefault()
      run(key === 'b' ? 'bold' : 'italic')
    } else if (key === 'k') {
      event.preventDefault()
      openLink()
    }
  }

  const tools: {
    label: string
    icon: LucideIcon
    pressed?: boolean
    action: () => void
    shortcut?: string
    popup?: boolean
    ref?: Ref<HTMLButtonElement>
  }[][] = [
    [
      { label: 'Bold', icon: Bold, pressed: format.bold, action: () => run('bold'), shortcut: 'Control+B' },
      { label: 'Italic', icon: Italic, pressed: format.italic, action: () => run('italic'), shortcut: 'Control+I' },
      {
        label: 'Link',
        icon: Link2,
        pressed: format.link || linkPopover.isOpen,
        action: () => (linkPopover.isOpen ? closeLink() : openLink()),
        shortcut: 'Control+K',
        popup: true,
        ref: linkPopover.triggerRef,
      },
    ],
    [
      { label: 'Heading', icon: Heading, pressed: format.heading, action: () => run('formatBlock', format.heading ? '<p>' : '<h1>') },
      { label: 'Bulleted list', icon: List, pressed: format.bullets, action: () => run('insertUnorderedList') },
      { label: 'Numbered list', icon: ListOrdered, pressed: format.numbers, action: () => run('insertOrderedList') },
    ],
    [
      { label: 'Undo', icon: Undo2, action: () => run('undo'), shortcut: 'Control+Z' },
      { label: 'Redo', icon: Redo2, action: () => run('redo'), shortcut: 'Control+Shift+Z' },
    ],
  ]

  let first = true

  return (
    <div className="email-composer" data-readonly={readOnly || undefined}>
      <div
        aria-controls={`${linkId}-editor`}
        aria-label="Formatting"
        className="email-toolbar"
        onKeyDown={onToolbarKeyDown}
        ref={toolbarRef}
        role="toolbar"
      >
        {tools.map((group, index) => (
          <div className="email-toolbar-group" key={index}>
            {group.map(({ label, icon: Icon, pressed, action, shortcut, popup, ref }) => {
              const tabIndex = first ? 0 : -1
              first = false
              return (
                <button
                  aria-expanded={popup ? linkPopover.isOpen : undefined}
                  aria-haspopup={popup ? 'dialog' : undefined}
                  aria-keyshortcuts={shortcut}
                  aria-label={label}
                  aria-pressed={pressed === undefined ? undefined : pressed}
                  className="email-tool"
                  disabled={readOnly}
                  key={label}
                  onClick={action}
                  // Keeps the editor selection while clicking a tool.
                  onMouseDown={(event) => event.preventDefault()}
                  ref={ref}
                  tabIndex={tabIndex}
                  title={label}
                  type="button"
                >
                  <Icon aria-hidden="true" size={17} strokeWidth={2} />
                </button>
              )
            })}
          </div>
        ))}
      </div>

      <div
        aria-labelledby={labelledBy}
        aria-multiline="true"
        aria-readonly={readOnly || undefined}
        className="email-editor"
        {...NO_WRITING_HELPERS}
        contentEditable={!readOnly}
        data-empty={empty || undefined}
        data-placeholder={placeholder}
        id={`${linkId}-editor`}
        onBlur={refresh}
        onInput={refresh}
        onKeyDown={onEditorKeyDown}
        onKeyUp={refresh}
        onMouseUp={refresh}
        onPaste={(event) => {
          // Pasted formatting from web pages and Word is dropped: plain text only.
          event.preventDefault()
          exec('insertText', event.clipboardData.getData('text/plain'))
          refresh()
        }}
        ref={editorRef}
        role="textbox"
        spellCheck
        suppressContentEditableWarning
        tabIndex={readOnly ? -1 : 0}
      />

      {linkPopover.phase !== 'closed'
        ? createPortal(
            <div
              aria-labelledby={`${linkId}-link-title`}
              className="wt-popover email-link-popover"
              data-state={linkPopover.phase}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  // Close only the popover, not the whole dialog.
                  event.preventDefault()
                  event.stopPropagation()
                  closeLink()
                }
              }}
              ref={linkPopover.panelRef}
              role="dialog"
              style={linkPopover.style}
            >
              <strong id={`${linkId}-link-title`}>{editingLink ? 'Edit link' : 'Add link'}</strong>
              <label htmlFor={`${linkId}-link`}>URL</label>
              <input
                aria-describedby={linkError ? `${linkId}-link-error` : undefined}
                aria-invalid={Boolean(linkError) || undefined}
                autoComplete="off"
                id={`${linkId}-link`}
                onChange={(event) => {
                  setLinkValue(event.target.value)
                  setLinkError('')
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    event.stopPropagation()
                    applyLink()
                  }
                }}
                placeholder="https://example.com"
                ref={linkInputRef}
                spellCheck={false}
                type="text"
                value={linkValue}
              />
              {linkError ? (
                <p className="email-link-error" id={`${linkId}-link-error`} role="alert">
                  {linkError}
                </p>
              ) : null}
              <div className="email-link-actions">
                {editingLink ? (
                  <button className="email-link-remove" onClick={removeLink} type="button">
                    Remove link
                  </button>
                ) : null}
                <button className="email-link-cancel" onClick={() => closeLink()} type="button">
                  Cancel
                </button>
                <button className="email-link-apply" onClick={applyLink} type="button">
                  Apply
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
